import {
  DatasetStyleCellFont,
  DatasetStyleColor,
  DatasetStyleRuleCondition,
  DatasetStyleRuleDatePredicate,
  DatasetStyleRuleNumberPredicate,
  DatasetStyleRuleStringPredicate,
  DatasetStyleRuleValue,
  tg,
} from '@infotech-soe/metaschema';
import mtkMatcher from '@infotech-soe/mia-toolkit-matcher';
import { isAfter, isBefore, parseJSON } from 'date-fns';
import { CSSProperties } from 'react';

import { DatasetPresetRule } from '../../../../models/user-config/DisplayModeConfig';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../../../../services/dataset/view-model/DatasetGridColumn';

const resolveNumber = (
  param: number,
  predicate: DatasetStyleRuleNumberPredicate
): boolean => {
  const empty = predicate.empty;
  const notEmpty = predicate.notEmpty;

  if ((empty && tg.isNothing(param)) || (notEmpty && tg.isSomething(param)))
    return true;

  const { before, after } = predicate;

  if (before === null && after === null) {
    return false;
  }
  if (before !== null && after !== null) {
    return param > before && param < after;
  }

  if (before !== null) {
    return param > before;
  } else if (after !== null) {
    return param < after;
  }
  return false;
};

const resolveString = (
  param: string,
  predicate: DatasetStyleRuleStringPredicate
): boolean => {
  const { pattern = '', caseSensitive } = predicate;
  const testValue = param !== null ? String(param).trim() : param;
  if (pattern) {
    return mtkMatcher(pattern, !caseSensitive)(testValue);
  }
  return false;
};

const toDate = (val?: Date | string | null): Date | null => {
  if (!val) {
    return null;
  }
  return tg.isDate(val) ? val : parseJSON(val);
};

export const getCellFontStyles = (
  cellProps: DatasetStyleCellFont
): Pick<CSSProperties, 'fontStyle' | 'fontWeight' | 'textDecoration'> => {
  const textDecorationStyle =
    cellProps.textUnderline && cellProps.textLineThrough
      ? 'underline line-through'
      : cellProps.textUnderline && !cellProps.textLineThrough
        ? 'underline'
        : !cellProps.textUnderline && cellProps.textLineThrough
          ? 'line-through'
          : 'none';

  return {
    fontStyle: cellProps.textItalic ? 'italic' : 'normal',
    fontWeight: cellProps.textBold ? 'bold' : 'normal',
    textDecoration: textDecorationStyle,
  };
};

export const resolveDate = (
  val: string | Date,
  predicate: DatasetStyleRuleDatePredicate
): boolean => {
  const testValue = toDate(val);
  const empty = predicate.empty;
  const notEmpty = predicate.notEmpty;

  if (
    (empty && tg.isNothing(testValue)) ||
    (notEmpty && tg.isSomething(testValue))
  )
    return true;

  if (!testValue) {
    return false;
  }
  const before = toDate(predicate.before);
  const after = toDate(predicate.after);
  if (!(before || after)) {
    return false;
  }

  if (before && after) {
    if (!isBefore(after, before)) {
      return !isBefore(testValue, before) && !isAfter(testValue, after);
    } else {
      return !isBefore(testValue, after) && !isAfter(testValue, before);
    }
  }
  if (before) {
    return !isBefore(testValue, before);
  }
  if (after) {
    return !isAfter(testValue, after);
  }
  return false;
};

const resolver = (
  format: DatasetColumnFormat | undefined,
  value: number | string | Date | null | undefined,
  predicate: DatasetStyleRuleValue,
  color: DatasetStyleColor
): CSSProperties | null => {
  const style = getCellFontStyles({ ...predicate });
  if (predicate.scope) {
    return predicate.scope.includes(value) ? { ...color, ...style } : null;
  }

  switch (format) {
    case DatasetColumnFormat.NUMBER:
      return resolveNumber(
        value as number,
        predicate as DatasetStyleRuleNumberPredicate
      )
        ? { ...color, ...style }
        : null;
    case DatasetColumnFormat.STRING:
      return resolveString(
        value as string,
        predicate as DatasetStyleRuleStringPredicate
      )
        ? { ...color, ...style }
        : null;
    case DatasetColumnFormat.DATE:
    case DatasetColumnFormat.DATE_TIME_SEC:
      return resolveDate(
        value as Date,
        predicate as DatasetStyleRuleDatePredicate
      )
        ? { ...color, ...style }
        : null;
    default:
      return null;
  }
};

export const getCellStyles = (
  format: DatasetColumnFormat | undefined,
  rule: DatasetPresetRule,
  value: number | string | Date | null | undefined
): DatasetStyleColor | object => {
  const { conditions } = rule;

  return conditions.reduce(
    (
      prevState: Partial<DatasetStyleColor>,
      condition: DatasetStyleRuleCondition
    ) => {
      const { color, value: predicate } = condition;

      return { ...prevState, ...resolver(format, value, predicate, color) };
    },
    {}
  );
};

export const getRowStyles = (
  rules: Record<string, DatasetPresetRule>,
  row: Record<string, any>,
  columns: DatasetGridColumn[]
): Partial<DatasetStyleColor> => {
  let rowStyles: Partial<DatasetStyleColor> = {};
  Object.keys(rules).find((key: string) => {
    let conditionIndex = 0;
    const format = columns.find(column => column.field === key)?.format;
    rules[key].conditions.forEach((condition, index) => {
      if (
        new Date(condition.value?.before).getTime() <=
          new Date(row[key]).getTime() &&
        new Date(condition.value?.after).getTime() >=
          new Date(row[key]).getTime()
      ) {
        conditionIndex = index;
      } else if (
        condition.value?.scope?.length &&
        condition.value?.scope[0] === row[key]
      ) {
        conditionIndex = index;
      } else if (
        typeof row[key] === 'string' &&
        condition.value?.pattern === row[key].trim()
      ) {
        conditionIndex = index;
      }
    });

    if (rules[key].conditions[conditionIndex]?.value?.isApplyRuleToRow) {
      const styles = getCellStyles(format, rules[key], row[key]);
      if (Object.keys(styles).length) {
        rowStyles = styles;
        return true;
      }
    }
  });

  return rowStyles;
};
